import { useState, useEffect } from 'react';
import { useLearningProgress } from '../../../hooks/useLearningProgress';

interface LearningProgressPanelProps {
  onClose: () => void;
}

export default function LearningProgressPanel({ onClose }: LearningProgressPanelProps) {
  const { progress, resetProgress, visitedCount, totalCount, percentage, nextSection, getLearningPath } = useLearningProgress();
  const [showReset, setShowReset] = useState(false);

  const levelInfo = (() => {
    if (percentage < 20) return { label: '初学者', color: '#34D399', icon: 'ri-seedling-line', desc: '刚刚开始探索RWA世界' };
    if (percentage < 40) return { label: '入门者', color: '#60A5FA', icon: 'ri-book-open-line', desc: '已掌握基础概念' };
    if (percentage < 60) return { label: '进阶者', color: '#F59E0B', icon: 'ri-award-line', desc: '深入了解行业知识' };
    if (percentage < 80) return { label: '专业者', color: '#F472B6', icon: 'ri-medal-line', desc: '具备专业级知识储备' };
    return { label: 'RWA专家', color: '#7C3AED', icon: 'ri-vip-crown-line', desc: '全面掌握RWA知识体系' };
  })();

  const paths = getLearningPath();

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm"></div>
      <div
        className="relative bg-white rounded-3xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-gray-100 shrink-0">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl" style={{ background: `${levelInfo.color}15` }}>
                <i className={`${levelInfo.icon} text-lg`} style={{ color: levelInfo.color }}></i>
              </div>
              <div>
                <h3 className="text-lg font-black text-gray-900">我的学习进度</h3>
                <p className="text-xs text-gray-400">{levelInfo.desc}</p>
              </div>
            </div>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer">
              <i className="ri-close-line text-gray-600"></i>
            </button>
          </div>

          {/* Level + Progress */}
          <div className="flex items-center gap-4 mb-3">
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-sm font-bold text-gray-700">
                  已浏览 <span style={{ color: levelInfo.color }}>{visitedCount}</span> / {totalCount} 个板块
                </span>
                <span className="text-sm font-black" style={{ color: levelInfo.color }}>{percentage}%</span>
              </div>
              <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{ width: `${percentage}%`, background: `linear-gradient(90deg, ${levelInfo.color}88, ${levelInfo.color})` }}
                ></div>
              </div>
            </div>
            <div className="text-center shrink-0">
              <div className="text-xs font-bold px-2.5 py-1 rounded-full text-white" style={{ background: levelInfo.color }}>
                {levelInfo.label}
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Next Step Recommendation */}
          {nextSection && (
            <div className="p-4 rounded-2xl border" style={{ background: `${levelInfo.color}08`, borderColor: `${levelInfo.color}25` }}>
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-compass-3-line text-sm" style={{ color: levelInfo.color }}></i>
                <span className="text-xs font-bold" style={{ color: levelInfo.color }}>推荐下一步</span>
              </div>
              <a
                href={`#${nextSection.id}`}
                onClick={onClose}
                className="flex items-center gap-3 cursor-pointer group"
              >
                <div className="w-8 h-8 flex items-center justify-center rounded-lg shrink-0" style={{ background: `${nextSection.color}15` }}>
                  <i className={`${nextSection.icon} text-sm`} style={{ color: nextSection.color }}></i>
                </div>
                <div className="flex-1">
                  <div className="text-sm font-bold text-gray-900 group-hover:text-[#7C3AED] transition-colors">{nextSection.title}</div>
                  <div className="text-xs text-gray-400">点击跳转至该板块</div>
                </div>
                <i className="ri-arrow-right-line text-gray-400 group-hover:text-[#7C3AED] transition-colors"></i>
              </a>
            </div>
          )}

          {/* Learning Path */}
          <div>
            <h4 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
              <i className="ri-route-line text-[#7C3AED]"></i>个性化学习路径建议
            </h4>
            <div className="space-y-2">
              {paths.map((path, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <div className="w-5 h-5 flex items-center justify-center rounded-full shrink-0 mt-0.5 text-xs font-bold text-white" style={{ background: levelInfo.color }}>
                    {i + 1}
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed">{path}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Section List */}
          <div>
            <h4 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
              <i className="ri-list-check-2 text-[#7C3AED]"></i>板块浏览记录
            </h4>
            <div className="grid grid-cols-2 gap-2">
              {progress.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  onClick={onClose}
                  className={`flex items-center gap-2 p-2.5 rounded-xl border transition-all cursor-pointer ${
                    s.visited ? 'border-transparent' : 'border-gray-100 hover:border-gray-200'
                  }`}
                  style={s.visited ? { background: `${s.color}10`, borderColor: `${s.color}20` } : {}}
                >
                  <div className="w-6 h-6 flex items-center justify-center rounded-lg shrink-0" style={{ background: s.visited ? `${s.color}20` : '#F3F4F6' }}>
                    <i className={`${s.icon} text-xs`} style={{ color: s.visited ? s.color : '#9CA3AF' }}></i>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className={`text-xs font-semibold truncate ${s.visited ? 'text-gray-800' : 'text-gray-400'}`}>{s.title}</div>
                  </div>
                  {s.visited && <i className="ri-check-line text-xs shrink-0" style={{ color: s.color }}></i>}
                </a>
              ))}
            </div>
          </div>

          {/* Reset */}
          <div className="pt-2 border-t border-gray-100">
            {!showReset ? (
              <button
                onClick={() => setShowReset(true)}
                className="text-xs text-gray-400 hover:text-gray-600 transition-colors cursor-pointer flex items-center gap-1"
              >
                <i className="ri-refresh-line"></i>重置学习进度
              </button>
            ) : (
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-500">确认重置所有进度？</span>
                <button
                  onClick={() => { resetProgress(); setShowReset(false); }}
                  className="text-xs text-red-500 font-bold cursor-pointer hover:text-red-600"
                >确认</button>
                <button
                  onClick={() => setShowReset(false)}
                  className="text-xs text-gray-400 cursor-pointer hover:text-gray-600"
                >取消</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
